import {createContext, useContext, type MouseEvent} from "react";
import {useLocation} from "@tanstack/react-router";

export type ScaffoldPreviewViewport = "desktop" | "mobile" | "tablet";

export type ScaffoldPreviewNavigation = {
    onNavigate: (href: string) => void;
    previewFrameElement: HTMLElement | null;
    viewport: ScaffoldPreviewViewport;
    virtualPathname: string;
    virtualSearch: string;
};

export const ScaffoldPreviewNavigationContext = createContext<ScaffoldPreviewNavigation | null>(null);

export function useScaffoldPreviewNavigation(): ScaffoldPreviewNavigation | null {
    return useContext(ScaffoldPreviewNavigationContext);
}

export function useScaffoldLocation(): {
    href: string;
    pathname: string;
    search: string;
} {
    const preview = useScaffoldPreviewNavigation();
    const location = useLocation();
    if (preview !== null) {
        return {
            href: `${preview.virtualPathname}${preview.virtualSearch}`,
            pathname: preview.virtualPathname,
            search: preview.virtualSearch,
        };
    }
    const searchStr = location.searchStr;
    const search =
        searchStr === "" || searchStr.startsWith("?") === true ? searchStr : `?${searchStr}`;
    return {
        href: `${location.pathname}${search}`,
        pathname: location.pathname,
        search: search,
    };
}

const PREVIEW_NAV_TOGGLE_SELECTOR = "button, [data-sidebar-collapsible-chevron]";

/**
 * Accordion chevrons and collapsible-list expand controls sit inside (or next to)
 * `<a>` tags. Preview navigation must not steal those clicks.
 */
export function isPreviewNavToggleClick(eventTarget: EventTarget | null): boolean {
    if (eventTarget instanceof Element !== true) {
        return false;
    }
    return eventTarget.closest(PREVIEW_NAV_TOGGLE_SELECTOR) !== null;
}

export function interceptPreviewAnchorClick(
    event: MouseEvent,
    preview: ScaffoldPreviewNavigation | null,
): boolean {
    if (preview === null) {
        return false;
    }
    const eventTarget = event.target;
    if (eventTarget instanceof Element !== true) {
        return false;
    }
    const anchor = eventTarget.closest("a");
    if (anchor === null) {
        return false;
    }
    const toggleControl = eventTarget.closest(PREVIEW_NAV_TOGGLE_SELECTOR);
    if (toggleControl !== null && anchor.contains(toggleControl) === true) {
        return false;
    }
    if (
        event.ctrlKey === true ||
        event.metaKey === true ||
        event.shiftKey === true ||
        event.altKey === true ||
        event.button !== 0
    ) {
        return false;
    }
    if (anchor.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        event.stopPropagation();
        return true;
    }
    const href = anchor.getAttribute("href");
    if (href === null || href === "" || href === "#") {
        return false;
    }
    event.preventDefault();
    event.stopPropagation();
    preview.onNavigate(href);
    return true;
}
